import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import mtamLogo from "../images/eterniq_logo.png";
import {
  Authenticator,
  ThemeProvider,
  createTheme,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { generateClient } from "aws-amplify/api";
import { updateUserProfile } from "../graphql/mutations";
import { provisionMyAccountDocument } from "../graphql/customQueries";
import { graphqlUserPool } from "../utilities/graphqlUserPool";
import { I18n } from "@aws-amplify/core";
import { translations } from "@aws-amplify/ui";
import { useUser } from "../contexts/UserContext";
import "../styleSheets/header.css";
import "../styleSheets/buttons.css";
import Modal from "../components/Modal";
import { getHomeRoute, logToConsole } from "../utilities/utilities";
import {
  SELECTED_PLAN_STORAGE_KEY,
  SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY,
  listBillingPlans,
  getUser,
  isActivePlan,
  isPaidPlan,
  shouldRedirectToCheckout,
  getFreePlanId,
} from "../utilities/billing";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";
import { useUIMessage } from "./ui/UIMessageProvider";
import type { ContactNavigationState } from "../types/navigation";
import { trackEvent } from "../utilities/analytics";
import {
  clearStoredActiveSessionId,
  createLoginSessionId,
  getCurrentAuthToken,
  getStoredActiveSessionId,
  logSessionEvent,
  setStoredActiveSessionId,
} from "../utilities/loginEvents";

function normalizePreferredLanguage(
  value: string | null | undefined,
): "en" | "es" {
  return String(value ?? "en")
    .trim()
    .toLowerCase() === "es"
    ? "es"
    : "en";
}

type UserPreferencesLanguageRecord = {
  UserPreferences?: {
    preferredLanguage?: string | null;
  } | null;
};

function Header() {
  I18n.putVocabularies(translations);
  I18n.putVocabulariesForLanguage("en", {
    "Create Account": "Register",
  });
  I18n.putVocabulariesForLanguage("es", {
    "Create Account": "Crear cuenta",
    "Sign In": "Iniciar sesión",
    "Sign Up": "Registrarse",
    Email: "Correo electrónico",

    Password: "Contraseña", // amazon-q-ignore

    "Confirm Password": "Confirmar contraseña", // amazon-q-ignore
    "Forgot your password?": "¿Olvidaste tu contraseña?",

    "Reset Password": "Restablecer contraseña", // amazon-q-ignore
    "Send code": "Enviar código",
    "Back to Sign In": "Volver a iniciar sesión",
  });

  const authTheme = createTheme({
    name: "custom-auth-buttons",
    tokens: {
      components: {
        button: {
          borderRadius: { value: "12px" },
          paddingInlineStart: { value: "24px" },
          paddingInlineEnd: { value: "24px" },
          paddingBlockStart: { value: "8px" },
          paddingBlockEnd: { value: "8px" },
          backgroundColor: { value: "#fff" },
          color: { value: "#ef4130" },
          borderColor: { value: "#ef4130" },
          borderWidth: { value: "1px" },
          _hover: { backgroundColor: { value: "#ffe9e6" } },
          _focus: { boxShadow: { value: "0 0 0 3px rgba(239,65,48,0.2)" } },
          primary: {
            backgroundColor: { value: "#fff" },
            color: { value: "#ef4130" },
            borderColor: { value: "#ef4130" },
            borderWidth: { value: "1px" },
            _hover: { backgroundColor: { value: "#ffe9e6" } },
          },
        },
      },
    },
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const registrationProcessingRef = useRef(false);
  const navigationCheckRef = useRef(false);
  const currentAuthSessionUserRef = useRef<string | null>(null);
  const pendingPostLoginRedirectRef = useRef(false);
  const inactiveRedirectHandledRef = useRef(false);
  const interactiveAuthStartedRef = useRef(false);
  const preferredLanguageAppliedUserRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("common");
  const primaryNavigationLabel = t("header.menu.primaryNavigation", {
    defaultValue: "Primary navigation",
  });
  const authDialogLabel = t("header.menu.signInRegister", {
    defaultValue: "Sign in / Register",
  });
  const trackAndNavigate = (eventName: string, to: string) => {
    trackEvent(eventName);
    setMenuOpen(false);
    requestNavigation(() => navigate(to));
  };

  const {
    user,
    isAuthenticated,
    isAuthLoading,
    isSigningOut,
    isUserDataLoading,
    isUserActive,
    signOut,
    refreshUserProfile,
  } = useUser();
  const { error } = useAuthenticator((context) => [context.error]);
  const { toast } = useUIMessage();
  const { requestNavigation } = useNavigationGuard();
  const userId = user?.userId ?? null;
  const userLoginId = user?.signInDetails?.loginId ?? null;
  const currentPath = location.pathname.toLowerCase();

  const authErrorMessage = (error ?? "") as string;

  const userAuthErrorPatterns = [
    "UserNotFoundException",
    "NotAuthorizedException",
    "UserNotConfirmedException",
    "PasswordResetRequiredException",
    "Incorrect username or password",
    "Incorrect username or password.",
    "User does not exist",
    "User is not confirmed",
    "Password reset required",
  ];

  const isUserAuthError =
    !!authErrorMessage &&
    userAuthErrorPatterns.some((p) => authErrorMessage.includes(p));

  const blockAuth =
    showAuth && !isAuthenticated && !!authErrorMessage && !isUserAuthError;

  const clearLocalAuthSessionState = useCallback(() => {
    setShowAuth(false);
    pendingPostLoginRedirectRef.current = false;
    window.localStorage.removeItem(SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY);
    window.localStorage.removeItem(SELECTED_PLAN_STORAGE_KEY);
    setMenuOpen(false);
  }, []);

  const openAuthModal = () => {
    interactiveAuthStartedRef.current = true;
    pendingPostLoginRedirectRef.current = true;
    window.localStorage.removeItem(SELECTED_PLAN_STORAGE_KEY);
    setShowAuth(true);
  };

  const closeAuthModal = () => {
    setShowAuth(false);
    if (!isAuthenticated) {
      pendingPostLoginRedirectRef.current = false;
    }
  };

  const handleSignOut = async () => {
    const sessionId = getStoredActiveSessionId();
    const authToken = await getCurrentAuthToken();

    await signOut();

    if (userId && sessionId) {
      await logSessionEvent({
        eventType: "LOGOUT",
        userId,
        sessionId,
        authToken,
      });
    }

    clearStoredActiveSessionId();
    clearLocalAuthSessionState();
    navigate("/");
  };

  const buildInactiveContactPrefill = useCallback(
    ({
      firstName,
      lastName,
      email,
    }: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    }): NonNullable<ContactNavigationState["contactPrefill"]> => ({
      name: `${firstName ?? ""} ${lastName ?? ""}`.trim(),
      email: email ?? "",
      message: t("contact.inactiveUserMessage"),
    }),
    [t],
  );

  const redirectInactiveUser = useCallback(
    async ({
      firstName,
      lastName,
      email,
    }: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    }) => {
      inactiveRedirectHandledRef.current = true;
      pendingPostLoginRedirectRef.current = false;

      try {
        await signOut();
        clearStoredActiveSessionId();
        clearLocalAuthSessionState();
        toast.info(t("auth.inactiveUser"));
        navigate("/ContactUs", {
          replace: true,
          state: {
            contactPrefill: buildInactiveContactPrefill({
              firstName,
              lastName,
              email,
            }),
            source: "inactiveUser",
          } satisfies ContactNavigationState,
        });
      } catch (error) {
        inactiveRedirectHandledRef.current = false;
        throw error;
      }
    },
    [
      buildInactiveContactPrefill,
      clearLocalAuthSessionState,
      navigate,
      signOut,
      t,
      toast,
    ],
  );

  useEffect(() => {
    const language = i18n.language?.toLowerCase().startsWith("es")
      ? "es"
      : "en";
    I18n.setLanguage(language);
  }, [i18n.language]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planId = (params.get("planId") ?? "").trim();
    if (planId) {
      window.localStorage.setItem(SELECTED_PLAN_STORAGE_KEY, planId);
    }
    if (currentPath === "/register" && !isAuthenticated) {
      interactiveAuthStartedRef.current = true;
      setShowAuth(true);
    }
  }, [currentPath, isAuthenticated, location.search]);

  const ensureUserRecord = async (authenticatedUserId: string) => {
    const normalizedUserId = authenticatedUserId.trim();
    if (!normalizedUserId) return null;

    // 1) If the user already exists, stop.
    const existing = await getUser(normalizedUserId);
    if (existing) {
      return existing;
    }

    // 2) Provision server-side. The server creates User, UserProfile and UserPreferences from the
    // verified token claims and chooses the initial plan itself; the client no longer sends a plan id or
    // seeds profile state, and there is nothing to roll back here (the server write is all-or-nothing).
    try {
      await graphqlUserPool({ query: provisionMyAccountDocument });
    } catch (e) {
      logToConsole("error", "provisionMyAccount failed; account not set up:", e);
      return null;
    }

    // 3) Final verification read
    return getUser(normalizedUserId);
  };

  const recordSuccessfulLoginActivity = async (userId: string) => {
    const amplifyClient = generateClient();
    await amplifyClient.graphql({
      query: updateUserProfile,
      variables: {
        input: {
          id: userId,
          lastSuccessfulLoginAt: new Date().toISOString(),
        },
      },
    });
  };

  useEffect(() => {
    if (
      !isAuthenticated ||
      isSigningOut ||
      !userId ||
      inactiveRedirectHandledRef.current
    ) {
      return;
    }
    if (navigationCheckRef.current) return;

    const processAuthRouting = async () => {
      navigationCheckRef.current = true;
      registrationProcessingRef.current = true;

      try {
        setShowAuth(false);

        const isNewLoginSession = currentAuthSessionUserRef.current !== userId;
        const shouldLogLoginSuccess =
          interactiveAuthStartedRef.current && isNewLoginSession;
        if (isNewLoginSession) {
          currentAuthSessionUserRef.current = userId;
          preferredLanguageAppliedUserRef.current = null;
        }

        // "Pay later" suppresses the checkout redirect until the next sign-in. The key stays in
        // storage so it survives reloads; an interactive sign-in or a sign-out ends it.
        if (shouldLogLoginSuccess) {
          window.localStorage.removeItem(SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY);
        }
        const skipCheckoutUntilNextLogin = !!window.localStorage.getItem(
          SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY,
        );

        const userRecord = await ensureUserRecord(userId);
        if (!userRecord) {
          logToConsole(
            "error",
            "Failed to ensure user record; skipping routing.",
          );
          await refreshUserProfile();
          return;
        }

        if (preferredLanguageAppliedUserRef.current !== userId) {
          const preferredLanguage = normalizePreferredLanguage(
            (userRecord as UserPreferencesLanguageRecord).UserPreferences
              ?.preferredLanguage,
          );
          window.localStorage.setItem("mtam.language", preferredLanguage);

          const currentLanguage = i18n.language?.toLowerCase().startsWith("es")
            ? "es"
            : "en";
          if (preferredLanguage !== currentLanguage) {
            await i18n.changeLanguage(preferredLanguage);
          }
          preferredLanguageAppliedUserRef.current = userId;
        }

        if (shouldLogLoginSuccess) {
          const sessionId = createLoginSessionId();
          setStoredActiveSessionId(sessionId);
          await logSessionEvent({
            eventType: "LOGIN_SUCCESS",
            userId,
            sessionId,
          });

          try {
            await recordSuccessfulLoginActivity(userId);
          } catch (loginActivityError) {
            logToConsole(
              "warn",
              "Failed to record successful login activity:",
              loginActivityError,
            );
          }

          interactiveAuthStartedRef.current = false;
        }

        if (userRecord.UserProfile?.isActive === false) {
          await redirectInactiveUser({
            firstName: userRecord.UserProfile.firstName,
            lastName: userRecord.UserProfile.lastName,
            email: userRecord.email,
          });
          return;
        }

        const refreshedUserData = await refreshUserProfile();
        if (refreshedUserData?.isUserActive === false) {
          await redirectInactiveUser({
            firstName: refreshedUserData.firstName,
            lastName: refreshedUserData.lastName,
            email: refreshedUserData.userEmail,
          });
          return;
        }

        const selectedPlanId = (
          window.localStorage.getItem(SELECTED_PLAN_STORAGE_KEY) ?? ""
        ).trim();

        if (currentPath === "/checkout") {
          return;
        }

        if (skipCheckoutUntilNextLogin) {
          return;
        }

        if (shouldRedirectToCheckout(userRecord)) {
          pendingPostLoginRedirectRef.current = false;
          const planId = (
            userRecord?.pendingPlanId ??
            selectedPlanId ??
            ""
          ).trim();
          const target = planId
            ? `/Checkout?planId=${encodeURIComponent(planId)}`
            : "/Checkout";
          setMenuOpen(false);
          navigate(target);
          return;
        }

        if (
          selectedPlanId &&
          selectedPlanId !== (userRecord?.userPlanId ?? "")
        ) {
          try {
            const plans = await listBillingPlans();
            const freePlanId = getFreePlanId(plans);
            const selectedPlan = plans.find(
              (plan) => (plan.planId ?? plan.id) === selectedPlanId,
            );
            if (
              selectedPlan &&
              isActivePlan(selectedPlan) &&
              isPaidPlan(selectedPlan) &&
              freePlanId &&
              (userRecord?.userPlanId ?? "") === freePlanId
            ) {
              pendingPostLoginRedirectRef.current = false;
              setMenuOpen(false);
              navigate(
                `/Checkout?planId=${encodeURIComponent(selectedPlanId)}`,
              );
            }
          } catch (planError) {
            logToConsole(
              "error",
              "Failed to validate selected plan before checkout redirect:",
              planError,
            );
          }
        }

        if (pendingPostLoginRedirectRef.current) {
          pendingPostLoginRedirectRef.current = false;
          if (
            currentPath === "/" ||
            currentPath === "/register" ||
            currentPath === "/dashboard"
          ) {
            return;
          }
          setMenuOpen(false);
          navigate("/Dashboard", { replace: true });
        }
      } catch (e) {
        logToConsole("error", "Failed to process authenticated routing:", e);
        await refreshUserProfile();
      } finally {
        registrationProcessingRef.current = false;
        navigationCheckRef.current = false;
      }
    };

    void processAuthRouting();
  }, [
    currentPath,
    isAuthenticated,
    isSigningOut,
    navigate,
    refreshUserProfile,
    redirectInactiveUser,
    i18n,
    userId,
    userLoginId,
  ]);

  useEffect(() => {
    // isAuthLoading: every page load starts in Amplify's "configuring" state before a stored
    // session resolves to "authenticated" - that is not a sign-out.
    if (isAuthenticated || isAuthLoading) return;
    currentAuthSessionUserRef.current = null;
    preferredLanguageAppliedUserRef.current = null;
    pendingPostLoginRedirectRef.current = false;
    inactiveRedirectHandledRef.current = false;
    interactiveAuthStartedRef.current = false;
    window.localStorage.removeItem(SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY);
  }, [isAuthenticated, isAuthLoading]);

  const handleLanguageChange = async (language: "en" | "es") => {
    i18n.changeLanguage(language);
    window.localStorage.setItem("mtam.language", language);
    setMenuOpen(false);
  };

  const isSpanish = i18n.language?.toLowerCase().startsWith("es");
  const canAccessProtectedApp =
    isAuthenticated && !isUserDataLoading && isUserActive !== false;
  const showPublicInfoLinks = !canAccessProtectedApp;
  const showAuthCta = !isAuthenticated;

  return (
    <header>
      <div className={"header-area"}>
        <div className={"header-container"}>
          <div className={"header-logo"}>
            <button
              type="button"
              className="header-logo-btn"
              aria-label={t("header.logoAlt")}
              onClick={() => {
                setMenuOpen(false);
                requestNavigation(() =>
                  navigate(getHomeRoute(canAccessProtectedApp)),
                );
              }}
            >
              <img src={mtamLogo} alt={t("header.logoAlt")} />
            </button>
          </div>

          <button
            type="button"
            className="header-burger"
            aria-label={t("header.menu.toggleMenu", {
              defaultValue: "Toggle menu",
            })}
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={() => setMenuOpen((v) => !v)}
          >
            ☰
          </button>

          <nav
            className={`header-links-container ${menuOpen ? "is-open" : ""}`}
            id="primary-navigation"
            aria-label={primaryNavigationLabel}
          >
            <div className="header-links">
              {canAccessProtectedApp && (
                <>
                  <button
                    type="button"
                    className="btn btn--tab"
                    onClick={() => {
                      setMenuOpen(false);
                      requestNavigation(() => navigate("/Dashboard"));
                    }}
                  >
                    {t("header.menu.dashboard")}
                  </button>

                  <button
                    type="button"
                    className="btn btn--tab"
                    onClick={() => {
                      setMenuOpen(false);
                      requestNavigation(() => navigate("/Profile"));
                    }}
                  >
                    {t("header.menu.profile")}
                  </button>

                  <button
                    type="button"
                    className="btn btn--tab"
                    onClick={() => {
                      setMenuOpen(false);
                      requestNavigation(() => navigate("/Preferences"));
                    }}
                  >
                    {t("header.menu.userSettings")}
                  </button>
                </>
              )}
            </div>

            <div className={"header-links"}>
              {showPublicInfoLinks && (
                <>
                  <button
                    type="button"
                    className="btn btn--tab"
                    onClick={() => trackAndNavigate("pricing_cta_clicked", "/Plans")}
                  >
                    {t("header.menu.pricing")}
                  </button>

                  <button
                    type="button"
                    className="btn btn--tab"
                    onClick={() => {
                      setMenuOpen(false);
                      requestNavigation(() => navigate("/Faqs"));
                    }}
                  >
                    {t("header.menu.faqs")}
                  </button>

                  <button
                    type="button"
                    className="btn btn--tab"
                    onClick={() =>
                      trackAndNavigate("contact_cta_clicked", "/ContactUs")
                    }
                  >
                    {t("header.menu.contact")}
                  </button>

                  {showAuthCta && (
                    <>
                      <div className="divider-header" />

                      <button
                        type="button"
                        className="btn btn--white btn--size-md auth-cta"
                        onClick={openAuthModal}
                        disabled={showAuth}
                        aria-haspopup="dialog"
                        aria-controls="public-auth-modal"
                        aria-expanded={showAuth}
                      >
                        {t("header.menu.signInRegister")}
                      </button>
                    </>
                  )}
                </>
              )}

              {canAccessProtectedApp && (
                <button
                  type="button"
                  className="btn btn--white btn--size-md auth-cta"
                  onClick={handleSignOut}
                >
                  {t("header.menu.signOut")}
                </button>
              )}

              <ThemeProvider theme={authTheme}>
                <Modal
                  isOpen={showAuth && !isAuthenticated}
                  onClose={closeAuthModal}
                  id="public-auth-modal"
                  ariaLabel={authDialogLabel}
                >
                  {blockAuth ? (
                    <div className="auth-error" role="alert">
                      {t("header.errorMessage.unableSignIn")}
                      {authErrorMessage && (
                        <div className="auth-error-details">
                          {authErrorMessage}
                        </div>
                      )}
                      <div className="auth-error-link">
                        <button
                          type="button"
                          className="btn btn--tab"
                          onClick={() => {
                            const contactState: ContactNavigationState = {
                              contactPrefill: {
                                message: t("header.errorMessage.authError"),
                              },
                              source: "authError",
                            };
                            closeAuthModal();
                            setMenuOpen(false);
                            requestNavigation(() =>
                              navigate("/ContactUs", {
                                state: contactState,
                              }),
                            );
                          }}
                        >
                          {t("header.menu.contact")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Authenticator
                      signUpAttributes={[
                        "email",
                        "given_name",
                        "family_name",
                        "nickname",
                      ]}
                      formFields={{
                        signUp: {
                          given_name: {
                            label: t("header.register.fName"),
                            placeholder: t("header.register.fNamePlaceHolder"),
                            order: 1,
                          },
                          family_name: {
                            label: t("header.register.lName"),
                            placeholder: t("header.register.lNamePlaceHolder"),
                            order: 2,
                          },
                          email: {
                            label: t("header.register.email"),
                            placeholder: t("header.register.emailPlaceHolder"),
                            order: 3,
                          },
                          nickname: {
                            label: t("header.register.pName"),
                            placeholder: t("header.register.pNamePlaceHolder"),
                            order: 4,
                          },
                        },
                      }}
                    />
                  )}
                </Modal>
              </ThemeProvider>

              <div className="language-toggle" aria-label="Language selector">
                <select
                  className="language-select"
                  aria-label="Language selector"
                  value={isSpanish ? "es" : "en"}
                  onChange={(event) =>
                    void handleLanguageChange(
                      event.target.value === "es" ? "es" : "en",
                    )
                  }
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;
